import { IBase, IDocument, IPaymentConfig } from '../../models'
import { IUserInfoState } from '../../store/states'
import { Language } from '../literals-service'
import { BaseHttpService } from './base'

class UserService extends BaseHttpService {
  public getInfo = async (): Promise<any> => {
    return this.get('users/me')
  }

  public updateInfo = async (user: IUserInfoState): Promise<any> => {
    return this.put(`users/me/${user.id}`, user)
  }

  public updateLanguage = async (userId: number, language: Language): Promise<any> => {
    return this.patch('users/me/language', {
      userId,
      language
    })
  }

  public sendDeviceToken = async (
    userId: number,
    deviceToken: string
  ): Promise<any> => {
    return this.patch('users/me', {
      userId,
      deviceToken,
      deviceType: 'web'
    })
  }

  public signOutDevice = async (): Promise<void> => {
    return this.delete('users/me/token')
  }

  public getIsUserExperienced = async (): Promise<boolean> => {
    return this.get('users/me/experienced')
  }

  public verifyEmail = async (): Promise<void> => {
    return this.patch('users/me/verify', null)
  }

  public getReasons = async (): Promise<IBase[]> => {
    return this.get('users/leave-reasons')
  }

  public deleteUser = async (reasonId: number): Promise<void> => {
    return this.delete(`users/me?reason-id=${reasonId}`)
  }

  public async updateTown(id: number): Promise<void> {
    return this.put('users/me/town', { id })
  }

  public postCustomer = async (paymentMethodId: string, serviceId: number): Promise<string> => {
    const body = { paymentMethodId: paymentMethodId, serviceId: serviceId }
    return this.post(`users/me/stripe-customer`, body)
  }

  public getPaymentConfig = async (serviceId?: number): Promise<IPaymentConfig> => {
    return this.get(`users/me/payment-config/${serviceId}`)
  }

  /** Step 1 of saving a card: a SetupIntent on the user's platform Customer, to confirm with Stripe.js. */
  public createSetupIntent = async (): Promise<{ setupIntentId: string, clientSecret: string }> => {
    return this.post('users/me/stripe-customer/setup-intent', {})
  }

  /** Step 2: Stripe.js confirmed it; the API records the card and clones it onto every operator of the town. */
  public confirmSetupIntent = async (setupIntentId: string): Promise<{ paymentMethodId: string, paymentMethodLastFour: string, customers: Array<{ customerId: string, accountId: string }> }> => {
    return this.post('users/me/stripe-customer/confirm', { setupIntentId })
  }

  public setPaymentMethodCustomer = async (paymentMethodId: string): Promise<string> => {
    return this.put(`users/me/stripe-customer/payment-method`, { paymentMethodId : paymentMethodId })
  }

  public getUserByEmail = async (email: string): Promise<any> => {
    return this.get(`users?email=${ encodeURIComponent(email) }`)
  }

  public getDocuments = async (): Promise<IDocument[]> => {
    return this.get('users/me/documents')
  }

  public postDocument = async (image: string, type: string, fileName: string, userName: string): Promise<string> => {
    return this.post(`users/me/documents`, { image, type, fileName, userName })
  }

  public deleteDocument = async (id: number): Promise<string> => {
    return this.delete(`users/me/documents/${id}`)
  }
}

export default new UserService()
